import React, { useState, MouseEvent, useEffect } from 'react';
import { AppBar as MUIAppBar, Box, Toolbar, IconButton, Typography, Menu, MenuItem } from '@mui/material';
import { Menu as MenuIcon, Settings as SettingsIcon } from '@mui/icons-material';
import { keyframes } from '@mui/system';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import logo from '../logo.png'
import { BRAND } from '../generated/brand';
import { useDrawer } from './context/DrawerContext';
import { User } from '../models/user';
import { clearAuthToken } from '../utils/auth-storage';
import { useMenu } from './MenuProvider';
import { buildAuthorizedModulesFromAuthUser } from '../utils/authorized-modules';
import { emitPermissionsRefreshed } from '../utils/permission-refresh';

const topBeeFlight = keyframes`
  0% {
    transform: translateX(calc(100% - 36px)) translateY(0px) rotate(0deg) scale(0.9);
    opacity: 0;
  }
  8% {
    opacity: 0.9;
  }
  20% {
    transform: translateX(80%) translateY(0px) rotate(0deg) scale(1);
  }
  40% {
    transform: translateX(60%) translateY(0px) rotate(0deg) scale(0.98);
  }
  60% {
    transform: translateX(40%) translateY(0px) rotate(0deg) scale(1);
  }
  84% {
    transform: translateX(16%) translateY(0px) rotate(0deg) scale(0.98);
    opacity: 0.95;
  }
  100% {
    transform: translateX(0%) translateY(0px) rotate(0deg) scale(0.9);
    opacity: 0;
  }
`;

const CustomAppBar: React.FC = () => {
  const { toggleDrawer } = useDrawer();
  const { setModules } = useMenu();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate()
  const [refreshingPermissions, setRefreshingPermissions] = useState(false);

  const logout = async () => {
    try {
      await axios.post('logout', {});
    } finally {
      clearAuthToken();
      sessionStorage.removeItem('isAuthenticated');
      sessionStorage.removeItem('modules');
      localStorage.removeItem('modules');
      navigate("/login");
    }
  }


  const handleMenuClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const refreshPermissions = async () => {
    try {
      setRefreshingPermissions(true);
      const response = await axios.get('user');
      const authUserData = response.data?.data ?? null;
      const userData = User.fromJSON(authUserData);
      const refreshedModules = buildAuthorizedModulesFromAuthUser(authUserData);

      setUser(userData);
      setModules(refreshedModules);
      sessionStorage.setItem('modules', JSON.stringify(refreshedModules));
      localStorage.setItem('modules', JSON.stringify(refreshedModules));
      emitPermissionsRefreshed();
    } catch (error) {
      console.error('Error refrescando permisos:', error);
    } finally {
      setRefreshingPermissions(false);
      handleMenuClose();
    }
  };

  const [user, setUser] = useState(new User());
  useEffect(() => {
    (async () => {
      try {
        const response = await axios.get('user');  // Ajusta la URL según sea necesario
        const { data } = response;
        if (data) {
          const userData = User.fromJSON(data.data);
          setUser(userData);
        }
      } catch (error) {
      }
    })();
  }, []);
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => {
        return response;
      },
      error => {
        if (error.response && error.response.status === 401) {
          navigate('/login');
        }

        return Promise.reject(error);
      }
    );
    return () => {
      axios.interceptors.response.eject(interceptor);
    }
  }, [navigate]);

  return (
    <MUIAppBar
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: '#01274d',
      }}
    >
      <Toolbar sx={{ position: 'relative' }}>
        <IconButton edge="start" color="inherit" aria-label="menu" onClick={toggleDrawer}>
          <MenuIcon />
        </IconButton>
        <img
          src={BRAND.web.assetUrls.logoMark || logo}
          alt="Logo"
          height={30}
          width={30}
          style={{ marginRight: '8px' }}
        />
        <Typography
          variant="h6"
          noWrap
          component="div"
          sx={{
            color: 'white',
            pr: 1,
            minWidth: 0,
            maxWidth: { xs: '45vw', sm: '58vw', md: 'none' },
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {BRAND.displayName}
        </Typography>
        <Box
          aria-hidden
          sx={{
            flexGrow: 1,
            minWidth: { xs: 48, sm: 88, md: 112 },
            height: { xs: 44, sm: 52, md: 60 },
            mx: { xs: 0.25, sm: 0.5 },
            position: 'relative',
            pointerEvents: 'none',
            overflow: 'hidden',
            display: 'block',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: '50%',
              height: 0,
              borderTop: {
                xs: '1px dashed rgba(255, 255, 255, 0.3)',
                sm: '2px dashed rgba(255, 255, 255, 0.35)',
              },
              transform: 'translateY(-50%)',
            }}
          />
          {/* Capa exterior centra la abeja en la misma altura que la línea; la animación solo mueve en X */}
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: '50%',
              width: '100%',
              transform: 'translateY(calc(-50% + 1px))',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                width: '100%',
                fontSize: { xs: 28, sm: 32, md: 40 },
                lineHeight: 1,
                animation: {
                  xs: `${topBeeFlight} 10s ease-in-out infinite`,
                  md: `${topBeeFlight} 14.4s ease-in-out infinite`,
                },
                filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.35))',
              }}
            >
              🐝
            </Box>
          </Box>
        </Box>
        <IconButton
          color="inherit"
          aria-controls="settings-menu"
          aria-haspopup="true"
          onClick={handleMenuClick}
          sx={{ ml: { xs: 'auto', md: 0 } }}
        >
          <SettingsIcon />
        </IconButton>

        <Menu
          id="settings-menu"
          anchorEl={anchorEl}
          keepMounted
          open={open}
          onClose={handleMenuClose}
        >
          <MenuItem >Hola! {user.username}</MenuItem>
          <MenuItem onClick={refreshPermissions} disabled={refreshingPermissions}>
            {refreshingPermissions ? 'Refrescando permisos...' : 'Refrescar permisos'}
          </MenuItem>
          {/* <MenuItem onClick={handleMenuClose}>Profile</MenuItem> */}
          <MenuItem onClick={logout}>Salir</MenuItem>
        </Menu>
      </Toolbar>
    </MUIAppBar>
  );
};

export default CustomAppBar;
